import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {cn} from '@/lib/utils'
import {ORDEN_FASES} from '@/lib/bateria'
import type {BateriaEvaluacionFase} from '@/hooks/bateria/use-bateria'

/** `bloqueada` es una `actual` parada por un hecho verificable del servidor. */
export type EstadoEtapa = 'hecha' | 'actual' | 'futura' | 'bloqueada'

export type Etapa = {
  fase: BateriaEvaluacionFase
  estado: EstadoEtapa
  /** `null` se pinta «—». */
  conteo: string | null
  /** La frase del bloqueo — solo con `estado: 'bloqueada'`. */
  bloqueo?: string
  /** Sin ella la etapa es un marcador `disabled`. */
  onIr?: () => void
}

function clasesDelPunto(estado: EstadoEtapa): string {
  if (estado === 'bloqueada') return 'bg-tone-amber-fg ring-2 ring-tone-amber-fg/30'
  if (estado === 'actual') return 'bg-primary ring-2 ring-primary/30'
  return estado === 'hecha' ? 'bg-primary' : 'bg-border'
}

function clasesDelConteo(estado: EstadoEtapa): string {
  if (estado === 'bloqueada') return 'text-tone-amber-fg'
  return estado === 'actual' ? 'text-primary' : 'text-muted-foreground'
}

export function BarraEscalera({etapas}: {etapas: Etapa[]}) {
  const {t} = useTranslation()
  const [desplegado, setDesplegado] = useState(false)
  const indiceActual = etapas.findIndex((e) => e.estado === 'actual' || e.estado === 'bloqueada')
  const actual = indiceActual === -1 ? etapas[etapas.length - 1] : etapas[indiceActual]

  return (
    <div data-testid="barra-escalera">
      <div className="flex items-center justify-between gap-3 md:hidden">
        <span className="min-w-0">
          <span className="block font-mono text-[10px] tracking-wider text-foreground uppercase">
            {t(`admin.bateria.barra.etapa.${actual.fase}`)}
          </span>
          <span className={cn('block font-mono text-[11px]', clasesDelConteo(actual.estado))}>
            {actual.conteo ?? '—'}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <span className="font-mono text-[11px] text-muted-foreground">
            {t('admin.bateria.barra.paso', {
              n: (indiceActual === -1 ? etapas.length - 1 : indiceActual) + 1,
              total: ORDEN_FASES.length,
            })}
          </span>
          <button
            type="button"
            data-testid="barra-escalera-detalle"
            aria-expanded={desplegado}
            aria-label={t('admin.bateria.barra.verEtapas')}
            onClick={() => setDesplegado((abierto) => !abierto)}
            className="font-mono text-[11px] text-muted-foreground">
            {desplegado ? '▾' : '▸'}
          </button>
        </span>
      </div>

      <ol
        className={cn(
          'flex flex-col gap-2 md:grid md:grid-cols-6 md:gap-x-3',
          'max-md:mt-2',
          !desplegado && 'max-md:hidden'
        )}>
        {etapas.map((etapa) => (
          <li key={etapa.fase} className="min-w-0">
            <button
              type="button"
              disabled={!etapa.onIr}
              onClick={etapa.onIr}
              aria-current={etapa.estado === 'actual' || etapa.estado === 'bloqueada' ? 'step' : undefined}
              data-testid={`barra-etapa-${etapa.fase}`}
              data-state={etapa.estado}
              className="w-full min-w-0 rounded-md text-left focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none enabled:cursor-pointer disabled:cursor-default">
              <span className="flex items-start gap-1.5">
                <span
                  aria-hidden="true"
                  className={cn('mt-1 size-2 shrink-0 rounded-full', clasesDelPunto(etapa.estado))}
                />
                <span
                  className={cn(
                    'font-mono text-[10px] leading-tight tracking-wider uppercase',
                    etapa.estado === 'futura' ? 'text-muted-foreground' : 'text-foreground'
                  )}>
                  {t(`admin.bateria.barra.etapa.${etapa.fase}`)}
                </span>
              </span>
              <span
                className={cn('mt-0.5 block font-mono text-[11px]', clasesDelConteo(etapa.estado))}
                data-testid={`barra-etapa-${etapa.fase}-conteo`}>
                {etapa.conteo ?? '—'}
              </span>
              {etapa.bloqueo && (
                <span
                  className="mt-0.5 block text-[11px] text-tone-amber-fg"
                  data-testid={`barra-etapa-${etapa.fase}-bloqueo`}>
                  {etapa.bloqueo}
                </span>
              )}
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}
